import * as React from 'react';
import AppInputError from './input-error';
import {ChangeEvent} from "react";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: string;
  inputmask?: string;
  position?: 'start' | 'end';
  children?: React.ReactElement;
  endIcon?: React.ReactElement | null;
  disabled?: boolean;
  label?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>)=>void;
  required?: boolean;
  useinputmask?: boolean;
  errorMessage?: string;
}

const AppInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({onChange, ...props}: InputProps) => {
    const [pos, setPos] = React.useState('');
    const IconPlacement = () => {
      return props.icon === 'envelope' ? (
        <div
          className={`absolute px-4 ${props.position === 'end' ? ` right-0 ` : ''}`}
        >
          {props.children}
        </div>
      ) : props.icon === 'password' ? (
        <div
          className={`absolute px-4 ${props.position === 'end' ? ` right-0 ` : ''}`}
        >
          {props.children}
        </div>
      ) : null;
    };

    React.useEffect(() => {
      switch (props.position) {
        case 'start':
          return setPos('pl-12');
        case 'end':
          return setPos('pr-12');
        default:
          return props.icon ? setPos('pl-12') : setPos('');
      }
    }, []);

    const applyMask = (text: string) => {
      if(!props.inputmask) return;
      let result = '';
      let textIndex = 0;

      for (let i = 0; i < props.inputmask.length && textIndex < text.length; i++) {
        const maskChar = props.inputmask[i];

        if (maskChar === '_') {
          if (textIndex < text.length) {
            result += text[textIndex];
            textIndex++;
          } else {
            break;
          }
        } else {
          result += maskChar;


          if (textIndex < text.length && text[textIndex] === maskChar) {
            textIndex++;
          }
        }
      }

      return result;
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>)=>{
      if(!onChange) return;
      if(!props.useinputmask){
        onChange(e)
      } else {
        const newValue = e.target.value;

        if(!newValue) {
          onChange(e);
          return;
        }
        const maskedValue = applyMask(newValue);
        if(!maskedValue) return;

        const mockEvent = {
          ...e,
          target: {
            ...e.target,
            value: maskedValue,
            name: e.target.name
          }
        };

        onChange(mockEvent);
      }
    }

    return (
      <div>
        {props.label ? (
          <div className="mb-1 flex items-center gap-2">
            <p className="font-semibold text-xs">{props.label}</p>
            <p className={`text-red-500 ${props.required ? '' : 'invisible'}`}>
              *
            </p>
          </div>
        ) : null}

        <div
          className={`flex justify-start relative items-center rounded-xl  px-4 py-2 h-12 ${props.errorMessage ? 'border-red-500 bg-red-50' : 'bg-[#FAFAFA]'}`}
        >
          {!props.position && props.icon && <IconPlacement />}
          {props.position === 'start' && props.icon && <IconPlacement />}
          <input
            type={props.type}
            className={`flex w-full bg-transparent hover:outline-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 text-sm ${pos}`}
            onChange={handleChange}
            {...props}
          />
          {props.position === 'end' && <IconPlacement />}
          {props.endIcon}
        </div>
        {props.errorMessage && <AppInputError text={props.errorMessage} />}
      </div>
    );
  },
);
AppInput.displayName = 'Input';

export default AppInput;
